<div class="tlu-page">
  <app-project-nav></app-project-nav>
  <app-page-header [title]="'testRuns.title' | translate" [subtitle]="'testRuns.subtitle' | translate">
    <ng-container right>
      <app-stat-group [items]="stats()"></app-stat-group>
      @if (!isReadOnly()) {
        <button
          type="button"
          class="tlu-primary"
          data-testid="testlauf-create"
          [attr.aria-label]="'testRuns.create' | translate"
          (click)="createRun()"
        >
          <mat-icon aria-hidden="true">add</mat-icon>
          {{ 'testRuns.create' | translate }}
        </button>
      }
    </ng-container>
  </app-page-header>

  @if (notice; as n) {
    <app-success-banner [messageKey]="n.key" [messageParams]="n.params" />
  }

  <div class="tlu-filters">
    <app-search-bar
      [placeholder]="'testRuns.filters.searchPlaceholder' | translate"
      [ariaLabel]="'testRuns.filters.search' | translate"
      [value]="query()"
      (valueChange)="onQueryInput($event)"
    />

    <app-filter-dropdown
      name="laufart"
      data-testid="filter-laufart"
      [value]="runTypeFilter()"
      [placeholder]="'testRuns.filters.allRunTypes' | translate"
      [options]="runTypeDropdownOptions()"
      (valueChange)="onRunTypeChange($event)"
    />

    <app-filter-dropdown
      name="art"
      [value]="artFilter()"
      [placeholder]="'testRuns.filters.allArts' | translate"
      [options]="artDropdownOptions()"
      (valueChange)="onArtChange($event)"
    />

    <app-filter-dropdown
      name="environment"
      [value]="environmentAktiv()"
      [placeholder]="'testRuns.filters.allEnvironments' | translate"
      [options]="environmentDropdownOptions()"
      (valueChange)="onEnvironmentChange($event)"
    />

    <app-filter-dropdown
      name="status"
      [value]="statusDropdownValue()"
      [placeholder]="'testRuns.filters.allStatuses' | translate"
      [options]="statusDropdownOptions()"
      (valueChange)="onStatusChange($event)"
    />

    <app-filter-dropdown
      name="assignee"
      [value]="assigneeFilter()"
      [placeholder]="'testRuns.filters.allAssignees' | translate"
      [options]="assigneeDropdownOptions()"
      (valueChange)="onAssigneeChange($event)"
    />

    <app-saved-views-menu
      [views]="savedViews()"
      [context]="savedViewContext"
      [projectId]="projectId"
      (viewSelected)="onSavedViewSelected($event)"
      (viewSaved)="onSavedViewSaved($event)"
      (viewDeleted)="onSavedViewDeleted($event)"
    ></app-saved-views-menu>

    @if (hasFilters()) {
      <app-reset-filter-button [label]="'testRuns.filters.reset' | translate" (resetClick)="resetFilters()" />
    }
  </div>

  @if (loadError()) {
    <p class="tlu-error" role="alert">{{ 'testRuns.loadError' | translate }}</p>
  }
  @if (statsError() && !loadError()) {
    <p class="tlu-error" role="alert">{{ 'testRuns.statsError' | translate }}</p>
  }
  @if (deleteError()) {
    <p class="tlu-error" role="alert">{{ deleteErrorKey() | translate }}</p>
  }

  <div class="tlu-card">
    <table class="tlu-table">
      <caption class="tlu-visually-hidden">{{ 'testRuns.tableCaption' | translate }}</caption>
      <thead>
        <tr>
          <th scope="col" class="tlu-col-id">{{ 'testRuns.columns.id' | translate }}</th>
          <th scope="col" class="tlu-col-title">{{ 'testRuns.columns.title' | translate }}</th>
          <th scope="col" class="tlu-col-art">{{ 'testRuns.columns.art' | translate }}</th>
          <th scope="col" class="tlu-col-env">{{ 'testRuns.columns.environment' | translate }}</th>
          <th scope="col" class="tlu-col-steps">{{ 'testRuns.columns.steps' | translate }}</th>
          <th scope="col" class="tlu-col-duration">{{ 'testRuns.columns.duration' | translate }}</th>
          <th scope="col" class="tlu-col-status">{{ 'testRuns.columns.status' | translate }}</th>
          <th scope="col" class="tlu-col-assignee">{{ 'testRuns.columns.assignee' | translate }}</th>
          <th scope="col" class="tlu-col-actions">{{ 'testRuns.columns.actions' | translate }}</th>
        </tr>
      </thead>
      <tbody>
        @for (run of runs(); track run.id) {
          @let live = liveRuns().get(run.id);
          @let progress = runProgress(run, live);
          <tr [attr.data-testid]="'testlauf-row-' + run.id" [class.tlu-row--with-band]="!!live">
            <td class="tlu-col-id">
              <code class="tlu-key" [class.tlu-key--trial]="run.trial" [title]="keyTitle(run) | translate">
                {{ run.key }}
              </code>
              @if (run.trial) {
                <div
                  class="tlu-trial-marker"
                  data-testid="lauf-probelauf-kennzeichen"
                  [attr.title]="'testRuns.trialPill.title' | translate"
                >
                  {{ 'testRuns.trialMarker' | translate }}
                </div>
              }
            </td>
            <td class="tlu-col-title">
              <span class="tlu-title">{{ run.title }}</span>
            </td>
            <td class="tlu-col-art"><app-art-pill [art]="run.art"></app-art-pill></td>
            <td class="tlu-col-env">
              @if (run.environment) {
                <app-umgebung-pill
                  [umgebung]="run.environment"
                  [isBranch]="isBranchEnvironment(run.source)"
                ></app-umgebung-pill>
              } @else {
                <span class="tlu-no-value">—</span>
              }
            </td>
            <td class="tlu-col-steps">
              <div class="tlu-progress" [attr.data-testid]="live ? 'live-run-progress' : null">
                <div
                  class="tlu-progress__track"
                  role="progressbar"
                  [attr.aria-valuenow]="progress.passed"
                  [attr.aria-valuemin]="0"
                  [attr.aria-valuemax]="progress.total"
                  [attr.aria-valuetext]="progress.label"
                  [attr.aria-label]="progress.labelKey | translate"
                >
                  <div
                    class="tlu-progress__bar"
                    [class.tlu-progress__bar--failed]="progress.failed"
                    [style.width.%]="progress.percent"
                  ></div>
                </div>
                <span class="tlu-progress__label">{{ progress.label }}</span>
              </div>
            </td>
            <td class="tlu-col-duration">{{ duration(run) }}</td>
            <td class="tlu-col-status">
              @if (isAborted(run)) {
                <span
                  class="tlu-abort-pill"
                  tabindex="0"
                  [matTooltip]="abortReason(run) || ('runSummary.noAbortReason' | translate)"
                >
                  <app-res-pill status="skipped" [label]="'testRuns.status.aborted' | translate" />
                </span>
              } @else {
                <app-res-pill
                  [status]="resPillStatus(run)"
                  [label]="'testResults.result.' + statusKey(run) | translate"
                />
              }
            </td>
            <td class="tlu-col-assignee">
              @let assignee = assigneeLabel(run);
              @if (assignee) {
                <div class="tlu-assignee">
                  <app-avatar [name]="assignee" [size]="28"></app-avatar>
                  <span class="tlu-assignee__name">{{ assignee }}</span>
                </div>
              } @else {
                <span class="tlu-no-value">—</span>
              }
            </td>
            <td class="tlu-col-actions">
              <div class="tlu-actions">
                <button
                  type="button"
                  class="tlu-action tlu-action--accent"
                  [attr.data-testid]="'testlauf-action-open-' + run.id"
                  [attr.aria-label]="'testRuns.actions.open' | translate: { key: run.key }"
                  (click)="openRun(run)"
                >
                  <mat-icon aria-hidden="true">chevron_right</mat-icon>
                </button>
                @if (!isArchived() && canAbort(run, live)) {
                  <button
                    type="button"
                    class="tlu-action tlu-action--danger"
                    [attr.data-testid]="'testlauf-action-abort-' + run.id"
                    [attr.aria-label]="'testRuns.actions.abort' | translate: { key: run.key }"
                    (click)="openAbort(run)"
                  >
                    <mat-icon aria-hidden="true">stop_circle</mat-icon>
                  </button>
                }
                @if (!isArchived()) {
                  <button
                    type="button"
                    class="tlu-action tlu-action--danger"
                    [attr.data-testid]="'testlauf-action-delete-' + run.id"
                    [attr.aria-label]="'testRuns.actions.delete' | translate: { key: run.key }"
                    (click)="askDelete(run)"
                  >
                    <mat-icon aria-hidden="true">delete</mat-icon>
                  </button>
                }
              </div>
            </td>
          </tr>
          @if (live) {
            <tr class="tlu-live-row">
              <td class="tlu-live-cell" colspan="9">
                <app-live-run-inline [pipeline]="live" />
              </td>
            </tr>
          }
        } @empty {
          <tr>
            <td class="tlu-empty" colspan="9">{{ 'testRuns.empty' | translate }}</td>
          </tr>
        }
      </tbody>
    </table>
  </div>

  <app-overview-pagination
    [pageIndex]="pageIndex()"
    [pageSize]="pageSize()"
    [pageSizeOptions]="pageSizeOptions"
    [totalCount]="totalElements()"
    (pageIndexChange)="onPageIndexChange($event)"
    (pageSizeChange)="onPageSizeChange($event)"
  />
</div>

@if (pendingDelete(); as run) {
  <app-confirm-dialog
    [visible]="true"
    [busy]="deleting()"
    [title]="'testRuns.deleteDialog.title' | translate"
    [message]="'testRuns.deleteDialog.message' | translate: { key: run.key, title: run.title }"
    [confirmLabel]="'testRuns.deleteDialog.confirm' | translate"
    [cancelLabel]="'testRuns.deleteDialog.cancel' | translate"
    (confirmed)="confirmDelete()"
    (cancelled)="cancelDelete()"
  ></app-confirm-dialog>
}

<app-test-run-abort-dialog
  [visible]="!!pendingAbort()"
  [submitting]="abortSubmitting()"
  [errorKey]="abortErrorKey()"
  (confirmed)="submitAbort($event)"
  (cancelled)="closeAbort()"
/>
